import React, { useState, useEffect } from 'react';

export default function FarmerInbox() {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [selectedHub, setSelectedHub] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [shareAll, setShareAll] = useState(false);

  useEffect(() => {
    fetchInvitations();
  }, []);

  const fetchInvitations = async () => {
    try {
      const res = await fetch('/api/farm/invitations', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      setInvitations(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleResponse = async (invitationId, status, hubId) => {
    setProcessingId(invitationId);
    if (status === 'active') {
      setSelectedHub(hubId);
      await fetchProducts();
      setShowProductModal(true);
      setProcessingId(null);
      return;
    }
    try {
      const res = await fetch(`/api/farm/invitation/${invitationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ status })
      });
      if (!res.ok) throw new Error(await res.text());
      fetchInvitations();
    } catch (error) {
      console.error(error);
      alert('Failed to process invitation');
    } finally {
      setProcessingId(null);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await fetch('/api/products/my-products', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      setProducts(Array.isArray(data) ? data : []);
      setSelectedProducts(data.map(p => p._id));
    } catch (error) {
      console.error(error);
    }
  };

  const confirmAcceptance = async () => {
    setProcessingId(selectedHub);
    try {
      const productsPayload = selectedProducts.map(pid => ({ productId: pid, commissionRate: null }));
      const res = await fetch('/api/farm/accept-invitation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ hubId: selectedHub, products: productsPayload, shareAll })
      });
      if (!res.ok) throw new Error(await res.text());
      setShowProductModal(false);
      fetchInvitations();
      alert('Successfully joined the hub!');
    } catch (error) {
      console.error(error);
      alert(error.message || 'Failed to accept invitation');
    } finally {
      setProcessingId(null);
    }
  };

  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId]);
  };

  if (loading) return <div className="text-center py-8">Loading invitations...</div>;

  const pendingInvitations = invitations.filter(i => i.status === 'pending');
  const acceptedInvitations = invitations.filter(i => i.status === 'active');

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-2">Hub Invitations</h1>
      <p className="text-gray-600 mb-8">Manage requests from hubs to sell your products through their network</p>

      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Pending Invitations ({pendingInvitations.length})</h2>
        {pendingInvitations.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">No pending invitations</div>
        ) : (
          <div className="space-y-4">
            {pendingInvitations.map(inv => (
              <div key={inv._id} className="bg-white rounded-lg shadow-md p-4 flex justify-between items-center">
                <div>
                  <div className="font-semibold text-lg">{inv.hubId?.hubSettings?.hubName || inv.hubId?.name}</div>
                  <div className="text-sm text-gray-500">{inv.hubId?.email}</div>
                  <div className="text-sm text-gray-500 mt-1">Proposed commission: {inv.defaultCommissionRate ?? inv.hubId?.hubSettings?.commissionRate ?? 10}%</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleResponse(inv._id, 'active', inv.hubId._id)} disabled={processingId === inv._id} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400">Accept</button>
                  <button onClick={() => handleResponse(inv._id, 'rejected')} disabled={processingId === inv._id} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400">Decline</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {acceptedInvitations.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold mb-4">Active Hub Connections ({acceptedInvitations.length})</h2>
          <div className="space-y-4">
            {acceptedInvitations.map(inv => (
              <div key={inv._id} className="bg-white rounded-lg shadow-md p-4 flex justify-between items-center">
                <div>
                  <div className="font-semibold text-lg">{inv.hubId?.hubSettings?.hubName || inv.hubId?.name}</div>
                  <div className="text-sm text-gray-500">Commission: {inv.commissionRate ?? inv.hubId?.hubSettings?.commissionRate ?? 10}%</div>
                  <div className="text-sm text-green-600 mt-1">{inv.shareAllProducts ? 'All products shared' : `${inv.productsShared?.length || 0} products shared`}</div>
                </div>
                <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-sm">Active</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {showProductModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Choose Products to Share</h2>
            <p className="text-gray-600 mb-4">Select which products this hub can sell on your behalf</p>
            <div className="mb-4">
              <label className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <input type="checkbox" checked={shareAll} onChange={(e) => { setShareAll(e.target.checked); if (e.target.checked) setSelectedProducts(products.map(p => p._id)); }} className="w-4 h-4" />
                <span className="font-medium">Share all products (current and future)</span>
              </label>
            </div>
            {!shareAll && (
              <div className="space-y-2 mb-4 max-h-96 overflow-y-auto border rounded p-3">
                <div className="font-medium mb-2">Select individual products:</div>
                {products.map(product => (
                  <label key={product._id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded">
                    <input type="checkbox" checked={selectedProducts.includes(product._id)} onChange={() => toggleProductSelection(product._id)} className="w-4 h-4" />
                    <div><div>{product.name}</div><div className="text-sm text-gray-500">${product.price}/{product.unit}</div></div>
                  </label>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
              <button onClick={() => setShowProductModal(false)} className="px-4 py-2 border rounded-md hover:bg-gray-50">Cancel</button>
              <button onClick={confirmAcceptance} disabled={!shareAll && selectedProducts.length === 0} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400">Confirm & Join Hub</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
